<template>
  <div class="d-flex flex-column align-items-center">

    <!-- for internal use only -->
    <video
      id="video"
      playsinline="playsinline"
      style="position: absolute; display: none"
      autoplay />

    <canvas
      id="canvas_w"
      width="640px"
      height="640px"
      style="display: none" />
    <canvas
      id="frame_canvas"
      style="display: none" />

    <audio
      id="camera_flash"
      style="display: none"
      :src="require('@/assets/audio/camera_flash.mp3')" />
    <!-- // for internal use only -->

    <app-title
      v-if="!scanInitiated"
      :title="'Selfie Analysis'"
      :backPage="'Start'" />

    <!-- When user initiated scan, show this block -->
    <div
      v-if="scanInitiated"
      class="flex-column flex-center mb-7 mt-xs-12 no-overflow">
      <div
        id="canvas_container"
        class="flex-column flex-center scanner vw-100 vw-xs-auto no-overflow bg-light radius-none radius-xs-small position-relative">

        <!-- Show preloader till AI is loaded -->
        <div class="spinner position-absolute z-plus-4 left-0 right-0 top-0 bottom-0 m-auto">
          <div />
          <div />
          <div />
          <div />
        </div>

        <!-- Show flash animation when user take selfie -->
        <div
          class="flash position-absolute w-100 h-100 top-0 left-0 right-0 bottom-0 z-plus-8"
          :class="{ 'flash--animated' : selfieTriggered }" />

        <!-- Show resulting photo -->
        <img
          v-if="photoMade"
          id="photo"
          src=""
          alt="photo"
          class="position-absolute w-100 h-100 top-0 left-0 right-0 bottom-0 z-plus-7 no-select">

        <!-- This is where AI magic happens -->
        <canvas
          id="canvas"
          class="w-100 h-100 z-plus-5" />

        <!-- Alerts for user -->
        <div
          class="w-100 flex-center position-absolute bottom-2 left-0 right-0 mx-auto z-plus-6"
          v-if="scanStarted">
          <scan-alert
            v-if="!dot_in_ellipse"
            :message="'Your face must be in oval zone'" />
          <scan-alert
            v-if="!box_is_ok && dot_in_ellipse"
            :message="'Please come closer'" />
        </div>
      </div>

      <!-- Buttons when user took photo -->
      <div
        v-if="photoMade"
        class="d-flex align-items-center w-100 px-3 px-xs-0 py-4">
        <button
          class="button button--outline button--iconed mr-2"
          @click="retakeScan"
          v-waves>
          <svg-icon icon="refresh" />
        </button>
        <button
          class="button button--main ml-2 flex-grow-1"
          @click="saveSelfie"
          :disabled="sendPhotoResponse"
          v-waves>
          <svg-icon
            icon="save"
            size="md"
            class="mr-3" />
          <span
            v-if="sendPhotoResponse"
            class="button__loading">Saving Photo</span>
          <span v-else>Save and Continue</span>
        </button>
      </div>

      <!-- Buttons when AI scans user face -->
      <div
        v-else
        class="d-flex align-items-center w-100 px-3 px-xs-0 py-4">
        <button
          class="button button--outline button--iconed mr-2"
          @click="cancelScan(true)"
          v-waves>
          <svg-icon icon="cross" />
        </button>
        <button
          class="button button--main ml-2 flex-grow-1"
          @click="takeSelfie"
          :disabled="!button_active"
          v-waves>
          <svg-icon
            icon="camera"
            size="md"
            class="mr-3" />
          <span>Take a Selfie</span>
        </button>
      </div>
    </div>

    <!-- Placeholder when user haven't start scan process yet -->
    <div
      v-else
      class="d-flex flex-column align-items-center mb-10">
      <scan-demo />

      <button
        class="button button--main"
        @click="startScan"
        v-waves>
        <svg-icon
          icon="camera"
          size="md"
          class="mr-3" />
        <span>Start Scan</span>
      </button>
    </div>

    <!-- Tips varies on scan process step -->
    <scan-tips :message="tipMessage" />

    <!-- At first step user can skip scan -->
    <button
      v-if="!scanInitiated"
      class="button button--outline mt-10"
      @click="skipQuiz"
      v-waves>
      <span>Skip to Quiz</span>
    </button>

  </div>
</template>

<script>
import ScanDemo from "@/components/ScanDemo"
import ScanTips from "@/components/ScanTips"
import ScanAlert from "@/components/ScanAlert"

export default {
  name: 'Scanner',
  components: {
    ScanDemo,
    ScanTips,
    ScanAlert,
  },
  data() {
    return {
      scanInitiated: false,
      scanStarted: false,
      selfieTriggered: false,
      photoMade: false,
      face_count: 0,
      dot_in_ellipse: false,
      box_is_ok: false,
      button_active: false,
    }
  },
  computed: {
    tipMessage() {
      if (!this.scanInitiated) {
        return 'Please ensure that your camera is working.'
      }

      if (this.scanInitiated && !this.photoMade) {
        return 'AI is scanning your face! Make sure all the dots are green and the square is blue.'
      }

      if (this.scanInitiated && this.photoMade) {
        return 'Move forward and answer a few questions for a better result or rescan your face.'
      }

      return ''
    },

    userId() {
      return this.$store.state.user.uid
    },

    sendPhotoResponse() {
      return this.$store.state.user.responses.sendPhoto
    },
  },
  methods: {
    skipQuiz() {
      this.$store.commit('config/SET_CURRENT_TRANSITION', this.isDesktop ? 'fade-in-left' : 'overlay-up-full')
      setTimeout(() => {
        this.$router.push({ name: 'Questions' })
      }, 100)
    },

    setScanSettings() {
      window.face.draw = {
        ellipse: true,
        dots: true,
        rectangle: true,
        rect_coeff: 1,
        btn_freeze_ms: 600,
      }
    },

    startScan() {
      this.scanInitiated = true
      this.$nextTick(() => {
        window.init_cam()

        window.face.cb = obj => {
          this.face_count = obj.face_count
          this.dot_in_ellipse = obj.dot_in_ellipse
          this.box_is_ok = obj.box_is_ok
          this.button_active = obj.button_active
        }

        document.querySelector('#video').classList.add('position-fixed')
      })
    },

    cancelScan(force) {
      // Might be forced to totally cancel scan process
      // and show user starting page (with button START SCAN)
      if (force) {
        this.scanInitiated = false
        this.scanStarted = false
      }

      this.$nextTick(() => {
        window.stop_cam()
      })
    },

    takeSelfie() {
      const self = this

      // Change global varaible (used to change layout)
      this.photoMade = true

      // Save canvas data to image
      this.$nextTick(() => {
        const photo = document.getElementById('photo')
        photo.src = document.getElementById('canvas_w').toDataURL()
      })

      // Simultaneously show flash effect...
      this.selfieTriggered = true
      setTimeout(() => { self.selfieTriggered = false }, 650)

      // ...and play sound effect
      var audio = document.getElementById('camera_flash')
      audio.play()

      // Stop scanning process
      this.cancelScan()
    },

    retakeScan() {
      // Launch scan process again
      this.startScan()

      // Remove data from image
      const photo = document.getElementById('photo')
      photo.src = ''

      // And switch varaible to hide this image and change buttons set
      this.photoMade = false
    },

    saveSelfie() {
      const self = this

      document.getElementById('frame_canvas').toBlob(blob => { // canvas_w // frame_canvas
        let formData = new FormData()
        formData.append(self.userId, blob, `${self.userId}.jpeg`)
        this.$store.dispatch('user/sendPhoto', formData)
      }, 'image/jpeg', 0.92)
    },

    watchForScanStart() {
      window.face.events.addEventListener('started', () => { this.scanStarted = true })
    },

    unwatchForScanStart() {
      this.scanStarted = false

      window.face.events.removeEventListener('started', {})
    }
  },
  mounted() {
    this.setScanSettings()
    this.watchForScanStart()
  },
  beforeDestroy() {
    this.unwatchForScanStart()
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/@core/globals';

.scanner {
  max-width: 520px;
  aspect-ratio: 1 / 1;
}

.spinner div {
  background: color('primary');
}

.flash {
  &--animated {
    @include animation($name: flash, $duration: .65s)
  }
}
</style>
